/** Piso de minutos configurável para o reengajamento por inatividade (nó de config). */
export const REENGAJAMENTO_MIN_MINUTOS = 1;

/**
 * Teto de minutos configurável para o reengajamento por inatividade (nó de config).
 *
 * 23h equivale ao timeout global de chatbot (`CHATBOT_TEMPO_LIMITE_INATIVIDADE`):
 * permite que um fluxo reproduca o comportamento do antigo `route-inatividade`
 * (que disparava no timeout global) usando 0 tentativas + minutos no teto. O valor
 * `0`/vazio tem significado especial: cai no timeout global do tenant (placeholder
 * "(Timeout Global)" no editor) — ver `clampReengajamentoMinutos` e o job.
 */
export const REENGAJAMENTO_MAX_MINUTOS = 23 * 60;

/**
 * Mínimo de cutucadas antes do desfecho. `0` = sem cutucada: o nó de config vira só
 * uma rota de inatividade (toca `route-sem-resposta` no tempo configurado, sem
 * mensagem nem botões). Subsume o antigo `route-inatividade` do Início (chatbot).
 */
export const REENGAJAMENTO_MIN_TENTATIVAS = 0;

/** Cutucadas assumidas quando o campo vem vazio/não-numérico (preserva o default histórico). */
export const REENGAJAMENTO_DEFAULT_TENTATIVAS = 1;

/**
 * Teto de cutucadas antes do desfecho.
 *
 * Cutucar o dia todo cansa o cliente, e no WhatsApp a janela de atendimento (24h)
 * conta a partir da última mensagem DO CLIENTE — mensagem da empresa não reinicia o
 * relógio. Com minutos altos (até 23h) e >1 tentativa, a 2ª cutucada cairia fora da
 * janela do WhatsApp; por isso o teto é 2. Quem quer só rotear sem cutucar usa 0.
 */
export const REENGAJAMENTO_MAX_TENTATIVAS = 2;

/** Tipo do nó que carrega as props de reengajamento, ligado ao Início via porta `cfg-reengajamento`. */
export const REENGAJAMENTO_CONFIG_NODE_TYPE = "reengajamentoConfig";

/** Nome da porta de saída (no Início) / entrada (no nó de config) que liga os dois nós. */
export const REENGAJAMENTO_CFG_PORT_NAME = "cfgReengajamento";

/**
 * Rota opcional do nó de config, acionada quando as tentativas de reengajamento
 * acabam e o cliente seguiu calado. Ligada = o fluxo segue por ela (tipicamente
 * uma transferência para atendimento humano — última cartada antes de desistir).
 * Desligada = o chat segue até o fechamento normal por inatividade.
 */
export const REENGAJAMENTO_ROUTE_SEM_RESPOSTA = "route-sem-resposta";

/**
 * Rota opcional do nó de config, acionada quando o cliente clica no botão de
 * ajuda. Ligada = o fluxo segue por ela (tipicamente uma transferência para
 * atendimento humano); desligada = o clique de ajuda não tem efeito.
 */
export const REENGAJAMENTO_ROUTE_AJUDA = "route-ajuda";

/**
 * Minutos de espera efetivos. `0` (vazio ou 0 explícito) é sentinela: significa
 * "usar o timeout global de chatbot do tenant" — o job resolve isso para
 * `timeoutMinutes` no lugar. Qualquer outro valor é preso à faixa permitida.
 */
export function clampReengajamentoMinutos(valor: unknown): number {
  const minutos = Number(valor);
  if (!Number.isFinite(minutos) || minutos <= 0) return 0;
  return Math.min(Math.max(Math.round(minutos), REENGAJAMENTO_MIN_MINUTOS), REENGAJAMENTO_MAX_MINUTOS);
}

/**
 * Quantidade efetiva de cutucadas. Vazio/não-numérico → default (1, cutuca uma vez).
 * Explícito `0` → 0 (sem cutucada, só rota). Preso à faixa [0, MAX].
 */
export function clampReengajamentoTentativas(valor: unknown): number {
  const tentativas = Number(valor);
  if (!Number.isFinite(tentativas)) return REENGAJAMENTO_DEFAULT_TENTATIVAS;
  return Math.min(
    Math.max(Math.round(tentativas), REENGAJAMENTO_MIN_TENTATIVAS),
    REENGAJAMENTO_MAX_TENTATIVAS
  );
}

/**
 * Localiza o nó de configuração de reengajamento ligado ao nó raiz (Início) via
 * a conexão `cfg-reengajamento`. Retorna `undefined` se não houver nó raiz ou
 * se o nó de config não estiver conectado.
 */
export function encontrarNoConfigReengajamento(nodes: Record<string, any> | undefined): any {
  if (!nodes) return undefined;
  const rootNode = Object.values(nodes).find((n: any) => n?.root === true) as any;
  if (!rootNode) return undefined;

  const conexao = rootNode.connections?.outputs?.find(
    (o: any) => o.name === REENGAJAMENTO_CFG_PORT_NAME
  );
  if (!conexao) return undefined;

  return nodes[conexao.node];
}

/** Atalho para obter apenas os `values` do nó de config de reengajamento (`{}` se não conectado). */
export function obterConfigReengajamento(nodes: Record<string, any> | undefined): Record<string, any> {
  return encontrarNoConfigReengajamento(nodes)?.values ?? {};
}

/** Gera um id curto e único para o nó de config migrado. */
function gerarNodeIdReengajamento(nodes: Record<string, any>): string {
  let id: string;
  do {
    id = "reeng_" + Math.random().toString(36).slice(2, 10);
  } while (id in nodes);
  return id;
}

/**
 * Migra, in-place, fluxos chatbot que usavam `route-inatividade` no nó Início para o
 * modelo do nó `reengajamentoConfig` (0 tentativas = só rota, sem cutucada).
 *
 * Para cada nó raiz (`start`) que tem uma conexão de saída `route-inatividade` E
 * ainda NÃO tem a porta `cfg-reengajamento` (já migrado/nativo):
 *  - spawna um nó `reengajamentoConfig` posicionado abaixo do Início;
 *  - cria a conexão `cfgReengajamento` (Início → config) nos dois sentidos;
 *  - move a conexão `route-inatividade` do Início para `route-sem-resposta` do config
 *    (reaponta o edge e a referência de `connections.inputs` do nó-alvo);
 *  - deixa `inatividade_tentativas = 0` e `inatividade_minutos` vazio (= timeout global,
 *    mesmo timing do `route-inatividade` legado, que disparava no timeout global).
 *
 * Idempotente: só migra quando há `route-inatividade` sem `cfg-reengajamento`. Reexecutar
 * é no-op. **Só deve rodar em fluxos chatbot (tipo 'atendimento')** — voz usa `route-inatividade`
 * e não tem nó de config; o chamador é responsável por filtrar por `tipo`.
 *
 * Retorna `true` se algo mudou. Usado pela migração de banco e pela normalização ao
 * abrir o editor (espelha `normalizarFluxoOpcoes`).
 */
export function normalizarFluxoInatividade(conteudo: any): boolean {
  const nodes = conteudo?.nodes;
  if (!nodes || typeof nodes !== "object") return false;

  let changed = false;

  for (const [startId, startNode] of Object.entries<any>(nodes)) {
    if (!startNode || startNode.type !== "start" || !startNode.root) continue;

    const outputs: any[] = startNode.connections?.outputs ?? [];
    const saidaInatividade = outputs.find((o) => o.name === "route-inatividade");
    // Sem route-inatividade, ou já tem cfg-reengajamento (nativo/migrado): nada a fazer.
    if (!saidaInatividade) continue;
    if (outputs.some((o) => o.name === REENGAJAMENTO_CFG_PORT_NAME)) continue;

    const configId = gerarNodeIdReengajamento(nodes);
    const start = startNode.position ?? { x: 0, y: 0 };

    const configNode: any = {
      id: configId,
      type: REENGAJAMENTO_CONFIG_NODE_TYPE,
      name: "Inatividade e reengajamento",
      position: { x: start.x, y: start.y + 260 },
      values: { inatividade_tentativas: 0 },
      // inatividade_minutos fica vazio (= timeout global), preservando o timing do
      // route-inatividade legado, que disparava no timeout global do tenant.
      connections: {
        inputs: [{ node: startId, port: REENGAJAMENTO_CFG_PORT_NAME }],
        outputs: [{ node: saidaInatividade.node, name: REENGAJAMENTO_ROUTE_SEM_RESPOSTA, type: "route" }],
      },
    };

    // 1. No Início: troca a saída route-inatividade pela cfg-reengajamento → config.
    startNode.connections.outputs = outputs.filter((o) => o !== saidaInatividade);
    startNode.connections.outputs.push({
      node: configId,
      name: REENGAJAMENTO_CFG_PORT_NAME,
      type: "cfg-reengajamento",
    });

    // 2. No nó-alvo: a entrada que apontava para start.route-inatividade passa a
    //    apontar para config.route-sem-resposta.
    const alvo = nodes[saidaInatividade.node];
    if (alvo) {
      const inputs: any[] = alvo.connections?.inputs ?? [];
      for (const inp of inputs) {
        if (inp.node === startId && inp.port === "route-inatividade") {
          inp.node = configId;
          inp.port = REENGAJAMENTO_ROUTE_SEM_RESPOSTA;
        }
      }
    }

    nodes[configId] = configNode;
    changed = true;
  }

  return changed;
}
